import { Controller } from '@hotwired/stimulus'
import type { FrameElement } from '@hotwired/turbo'
import { FetchRequest } from '@rails/request.js'

interface EmptyAuthCheckValue {}

interface InvalidAuthCheckResponse {
  validCredentials: false
}

interface ValidAuthCheckResponse {
  otp: {
    configured: boolean
    rememberOtpVerified: boolean
    required: boolean
  }
  validCredentials: true
}

// NOTE: This data shape should match the return value of
// the `Users::SessionsController#require_otp` action
type AuthCheckResponse = InvalidAuthCheckResponse | ValidAuthCheckResponse

export default class LoginFormController extends Controller<HTMLFormElement> {
  declare initialRememberedValue: boolean
  declare modalFrame: FrameElement
  declare authCheckEndpointValue: string
  declare authCheckResponseValue: EmptyAuthCheckValue | AuthCheckResponse
  declare readonly emailTarget: HTMLInputElement
  declare readonly otpAttemptTarget: HTMLInputElement
  declare readonly otpModalTemplateTarget: HTMLTemplateElement
  declare readonly otpRememberTarget: HTMLInputElement
  declare readonly passwordTarget: HTMLInputElement
  declare readonly submitTarget: HTMLButtonElement

  static targets = [
    'email',
    'otpAttempt',
    'otpModalTemplate',
    'otpRemember',
    'password',
    'submit'
  ]

  static values = {
    authCheckEndpoint: String,
    authCheckResponse: Object,
    initialRemembered: Boolean
  }

  // Lifecycle methods

  connect() {
    this.#assignModalFrame()
    this.element.addEventListener('submit', this.#handleSubmit.bind(this))
  }

  disconnect() {
    this.element.removeEventListener('submit', this.#handleSubmit.bind(this))
  }

  otpRememberTargetConnected(element: HTMLInputElement) {
    this.initialRememberedValue = element.checked
  }

  submitTargetConnected(element: HTMLButtonElement) {
    element.dataset.resetsWith = element.innerText
  }

  // Value change callback methods

  authCheckResponseValueChanged() {
    const response = this.authCheckResponseValue

    // Bail if the value is empty
    if (!('validCredentials' in response)) return

    // If any of the following conditions are met,
    // submit the form and let the backend handle the response:
    // * email/password credentials are incorrect
    // * 2FA isn't required
    // * 2FA is required but not configured
    if (
      !response.validCredentials ||
      !response.otp.required ||
      (response.otp.required && !response.otp.configured)
    ) {
      this.submit()
      return
    }

    // If we remember the user's OTP token, sign the user in directly
    if (this.#shouldRememberOtp(response)) {
      this.submit()
      return
    }

    // Otherwise, open the OTP modal
    this.#openOtpModal()
  }

  // Public methods

  reset() {
    this.otpRememberTarget.checked = this.initialRememberedValue
    this.submitTarget.innerText = this.submitTarget.dataset.resetsWith
    this.submitTarget.disabled = false
    this.authCheckResponseValue = {}
  }

  submit() {
    this.element.submit()
  }

  // Private methods

  #assignModalFrame() {
    const modalFrame = document.getElementById('modal-frame')
    if (!modalFrame) throw new Error('Modal frame not found')
    this.modalFrame = modalFrame as FrameElement
  }

  #handleAuthCheckFailure() {
    alert(`
      There was a problem verifying your credentials. Please try again.
      If this problem persists, please contact your organization\'s Advisor Squared administrator.
    `)

    throw new Error('Could not perform auth check.')
  }

  #handleSubmit(event: SubmitEvent) {
    const authChecked = 'validCredentials' in this.authCheckResponseValue

    this.submitTarget.innerText = this.submitTarget.dataset.submitsWith

    if (!authChecked) {
      event.preventDefault()
      this.#performAuthCheck()
    }
  }

  #openOtpModal() {
    const templateContent = this.otpModalTemplateTarget.content.cloneNode(true) as DocumentFragment
    const modalContent = templateContent.firstElementChild.firstElementChild
    this.modalFrame.appendChild(modalContent)
  }

  async #performAuthCheck() {
    try {
      this.authCheckResponseValue = {}

      const body = JSON.stringify({
        email: this.emailTarget.value,
        password: this.passwordTarget.value,
      })

      const response = await new FetchRequest('post', this.authCheckEndpointValue, { body }).perform()

      if (!response.ok) this.#handleAuthCheckFailure()

      this.authCheckResponseValue = await response.json
    } catch {
      this.#handleAuthCheckFailure()
    }
  }

  #shouldRememberOtp(response: ValidAuthCheckResponse) {
    return response.otp.rememberOtpVerified
  }
}
